import React from 'react';
import { inputStyle } from '../insurance/formBits';
import { formatMoney } from '../../insurance/format';
import { formatLongDate } from '../../dateOnly';

// One row per service line. In edit mode each field reports a patch through
// onChange(lineId, patch) using the API's PUT field names (cptCode, modifiers,
// units, charge, diagnosisPointers). `canEditCodes` is false for therapists: the
// office owns codes, units and charges; therapists still fix modifiers and pointers.
const th = { padding: '6px 8px', textAlign: 'left', color: 'var(--primary-color)', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 12 };
const td = { padding: '6px 8px', verticalAlign: 'top', fontSize: 13 };
const small = { ...inputStyle, width: 90, padding: '6px 8px' };

export default function ClaimLinesEditor({ lines, diagnosisCodes = [], editable, canEditCodes = true, showAmounts, onChange, errors = {} }) {
  const codesEditable = editable && canEditCodes;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--secondary-color)' }}>
            {['#', 'Date of service', 'CPT', 'Modifiers', 'Units', 'Charge', 'Diagnosis pointers'].map((h) => <th key={h} style={th}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id} style={{ borderTop: '1px solid var(--border-color)' }}>
              <td style={td}>{l.line_number}</td>
              <td style={td}>{formatLongDate(l.date_of_service)}</td>
              <td style={td}>
                {codesEditable
                  ? <input aria-label="CPT code" style={small} defaultValue={l.cpt_code} onChange={(e) => onChange(l.id, { cptCode: e.target.value.toUpperCase() })} />
                  : l.cpt_code}
              </td>
              <td style={td}>
                {editable
                  ? <input aria-label="Modifiers" style={small} defaultValue={(l.modifiers || []).join(', ')} placeholder="95, GT"
                           onChange={(e) => onChange(l.id, { modifiers: e.target.value.split(',').map((m) => m.trim().toUpperCase()).filter(Boolean) })} />
                  : (l.modifiers || []).join(', ') || '—'}
              </td>
              <td style={td}>
                {codesEditable
                  ? <input aria-label="Units" type="number" min="1" step="1" style={{ ...small, width: 60 }} defaultValue={l.units} onChange={(e) => onChange(l.id, { units: Number(e.target.value) })} />
                  : l.units}
              </td>
              <td style={td}>
                {!showAmounts ? '' : codesEditable
                  ? <input aria-label="Charge" type="number" min="0" step="0.01" style={small} defaultValue={l.charge} onChange={(e) => onChange(l.id, { charge: Number(e.target.value) })} />
                  : formatMoney(l.charge)}
              </td>
              <td style={td}>
                {editable
                  ? diagnosisCodes.map((code, i) => (
                    <label key={i} style={{ marginRight: 10, fontSize: 12, whiteSpace: 'nowrap' }}>
                      <input type="checkbox" aria-label={`Pointer ${i + 1}`} checked={(l.diagnosis_pointers || []).includes(i + 1)}
                             onChange={(e) => {
                               const set = new Set(l.diagnosis_pointers || []);
                               if (e.target.checked) set.add(i + 1); else set.delete(i + 1);
                               onChange(l.id, { diagnosisPointers: [...set].sort((a, b) => a - b) });
                             }} />
                      {' '}{i + 1}: {code}
                    </label>
                  ))
                  : (l.diagnosis_pointers || []).map((p) => `${p}: ${diagnosisCodes[p - 1] || '?'}`).join(', ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {errors.lines && <div style={{ color: 'var(--error-color)', fontSize: 12, marginTop: 4 }}>{errors.lines}</div>}
    </div>
  );
}
